import { useCallback, useRef, useState } from "react";
import {
  SubjectRebindPanel,
  type SubjectRebindSaveOpts,
} from "@/components/timetable-import/SubjectRebindPanel";
import {
  timetableApi,
  type RebindCategory,
  type SubjectRebindPreview,
} from "@/services/api/timetable";
import { extractErrorMessage } from "@/utils/errorHandler";

type AnchorTarget = EventTarget | { getBoundingClientRect: () => DOMRect };
type AnchorEvent = { currentTarget: AnchorTarget };

export function useEntryFieldRebind(onSaved?: () => void) {
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const [rebind, setRebind] = useState<{
    preview: SubjectRebindPreview;
    anchor: DOMRect;
    source?: RebindCategory;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const open = useCallback(
    async (entryId: string, field?: RebindCategory, event?: AnchorEvent) => {
      const el = event?.currentTarget as
        { getBoundingClientRect?: () => DOMRect } | undefined;
      const anchor =
        el?.getBoundingClientRect?.() ?? new DOMRect(24, 120, 80, 40);
      setLoadError(null);
      try {
        const preview = await timetableApi.previewSubjectRebind(entryId);
        setError(null);
        setRebind({ preview, anchor, source: field });
      } catch (err) {
        setLoadError(
          extractErrorMessage(err, {
            loadError: "Katalog konnte nicht geladen werden",
          })
        );
      }
    },
    []
  );

  const persist = useCallback(
    async (opts: SubjectRebindSaveOpts) => {
      if (!rebind) return;
      setSaving(true);
      setError(null);
      try {
        await timetableApi.rebindSubject(rebind.preview.entry_id, {
          subject_id: opts.createTarget ? undefined : opts.targetId,
          target_id: opts.createTarget ? undefined : opts.targetId,
          source_category: opts.sourceCategory,
          target_category: opts.targetCategory,
          include_siblings: opts.includeSiblings,
          create_alias: opts.createAlias,
          create_target: opts.createTarget,
          create_name: opts.createName,
        });
        setRebind(null);
        onSavedRef.current?.();
      } catch (err) {
        setError(
          extractErrorMessage(err, {
            loadError: "Konnte nicht gespeichert werden",
            saveError: "Konnte nicht gespeichert werden",
          })
        );
      } finally {
        setSaving(false);
      }
    },
    [rebind]
  );

  const panel = rebind ? (
    <SubjectRebindPanel
      preview={rebind.preview}
      anchor={rebind.anchor}
      initialSource={rebind.source}
      saving={saving}
      error={error}
      onSave={(opts) => {
        void persist(opts);
      }}
      onClose={() => {
        if (!saving) setRebind(null);
      }}
    />
  ) : null;

  return { open, panel, isOpen: Boolean(rebind), loadError };
}
